import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

export const API_BASE_URL: string =
  process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1";

export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: "alcazar.admin.accessToken",
  REFRESH_TOKEN: "alcazar.admin.refreshToken",
  USER: "alcazar.admin.user",
} as const;

export function getAccessToken(): string | null {
  return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
}

export function setAccessToken(token: string): void {
  localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, token);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, token);
}

export function clearAuthStorage(): void {
  localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
}

const SIGN_IN_ROUTE = "#/auth/sign-in";

function isOnSignInRoute(): boolean {
  return window.location.hash.startsWith("#/auth");
}

function redirectToSignIn(): void {
  if (!isOnSignInRoute()) {
    window.location.assign(SIGN_IN_ROUTE);
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

function pruneEmptyParams(params: unknown): unknown {
  if (!params || typeof params !== "object") return params;
  const entries = Object.entries(params as Record<string, unknown>).filter(
    ([, value]) => value !== "" && value !== undefined && value !== null
  );
  return Object.fromEntries(entries);
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.params) {
    config.params = pruneEmptyParams(config.params);
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      clearAuthStorage();
      redirectToSignIn();
    }
    return Promise.reject(error);
  }
);
